import React, { FC, useState } from 'react';
import { Autocomplete, Button, ListItemText, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MaterialMacro, MaterialMacroOption } from 'models/material-macro';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type UseMaterialMacroDialogProps = {
    open: boolean;
    onClose: () => void;
    // Every macro of the lab, paired with whether it can be applied to the current customer.
    // Unusable ones render disabled with the reason instead of being hidden.
    materialMacroOptions: MaterialMacroOption[];
    onAddMaterialMacro: (materialMacro: MaterialMacro) => void;
    isLoading?: boolean;
};

// User-initiated macro picker, opened from the "Use macro" button. Unlike the action-linked
// MaterialMacroDialog it is always dismissible: nothing downstream waits on a choice.
const UseMaterialMacroDialog: FC<UseMaterialMacroDialogProps> = ({
    open,
    onClose,
    materialMacroOptions,
    onAddMaterialMacro,
    isLoading,
}: UseMaterialMacroDialogProps) => {
    const { t: translate } = useTranslation(['actions-materials', 'common']);
    const [selectedOption, setSelectedOption] = useState<MaterialMacroOption | null>(null);

    const handleClose = () => {
        setSelectedOption(null);
        onClose();
    };

    const handleAdd = () => {
        if (!selectedOption) {
            return;
        }
        onAddMaterialMacro(selectedOption.macro);
        handleClose();
    };

    return (
        <DHDialog
            title={translate('actions-materials:SELECT_MATERIAL_MACRO')}
            maxWidth="sm"
            fullWidth
            open={open}
            onClose={handleClose}
            actions={
                <>
                    <Button onClick={handleClose} data-test="use-material-macro-dialog.cancel-btn">
                        {translate('common:CANCEL')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!selectedOption || isLoading}
                        onClick={handleAdd}
                        data-test="use-material-macro-dialog.add-btn"
                    >
                        {translate('common:ADD')}
                    </Button>
                </>
            }
        >
            <Autocomplete
                id="use-material-macro-autocomplete"
                options={materialMacroOptions}
                value={selectedOption}
                loading={isLoading}
                onChange={(_event, option: MaterialMacroOption | null) => setSelectedOption(option)}
                getOptionLabel={(option: MaterialMacroOption) => option.macro.name || ''}
                isOptionEqualToValue={(option, value) => option.macro._id === value.macro._id}
                getOptionDisabled={(option: MaterialMacroOption) => !option.isSelectable}
                noOptionsText={translate('actions-materials:NO_MATERIAL_MACROS_AVAILABLE')}
                renderOption={(props, { macro, isSelectable, missingMaterialNames }) => (
                    // MUI dims a disabled option to 38% opacity, which would wash out the
                    // explanation - keep it legible and convey the state with color instead.
                    <li {...props} key={macro._id} style={{ opacity: 1 }}>
                        <ListItemText
                            primary={macro.name}
                            primaryTypographyProps={isSelectable ? undefined : { color: 'text.disabled' }}
                            secondary={
                                isSelectable
                                    ? undefined
                                    : translate('actions-materials:MACRO_MATERIALS_NOT_IN_PRICE_LIST', {
                                          materials: missingMaterialNames.join(', '),
                                      })
                            }
                            secondaryTypographyProps={{ color: 'warning.main' }}
                        />
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        autoFocus
                        label={translate('actions-materials:MATERIAL_MACRO')}
                        fullWidth
                        margin="dense"
                        data-test="use-material-macro-dialog.autocomplete"
                    />
                )}
            />
        </DHDialog>
    );
};

export default UseMaterialMacroDialog;
